import { useSelector } from 'react-redux';

function normalizePercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.min(100, Math.max(0, Math.round(Number(value))));
}

/**
 * Banner: section_progress (or completedSectionsPercentage fallback), sections_completed,
 * total_sections from API. Optimistic slice used only while questionnaire flow is mounted.
 */
export default function useMedicalQuestionnaireProgress() {
  const questionnaireCompleted = useSelector(
    (s) => s.user.medical_questionnaire_completed === true
  );
  const sectionProgress = useSelector((s) => s.user.section_progress);
  const completedSectionsPercentage = useSelector(
    (s) => s.user.completedSectionsPercentage
  );
  const sectionsCompleted = useSelector((s) => s.user.sections_completed);
  const profileCompletedSections = useSelector((s) => s.user.totalCompletedSections);
  const profileTotalSections = useSelector((s) => s.user.totalSections);
  const sliceTotalSections = useSelector(
    (s) => s.medicalQuestionnaireProgress?.totalSections ?? 0
  );
  const optimisticPercent = useSelector(
    (s) => s.medicalQuestionnaireProgress?.optimisticCompletionPercentage
  );
  const optimisticFilledSections = useSelector(
    (s) => s.medicalQuestionnaireProgress?.optimisticFilledSections ?? 0
  );

  const apiPercent =
    normalizePercent(sectionProgress) ?? normalizePercent(completedSectionsPercentage);
  const optimistic = normalizePercent(optimisticPercent);
  const hasActiveOptimistic = optimistic != null;

  const totalSections =
    profileTotalSections != null &&
    Number.isFinite(Number(profileTotalSections)) &&
    Number(profileTotalSections) > 0
      ? Number(profileTotalSections)
      : sliceTotalSections;

  const percent = questionnaireCompleted
    ? 100
    : hasActiveOptimistic
      ? Math.max(apiPercent ?? 0, optimistic)
      : (apiPercent ?? 0);

  const apiFilled =
    sectionsCompleted != null && Number.isFinite(Number(sectionsCompleted))
      ? Math.max(0, Math.round(Number(sectionsCompleted)))
      : profileCompletedSections != null && Number.isFinite(Number(profileCompletedSections))
        ? Math.max(0, Math.round(Number(profileCompletedSections)))
        : null;

  const filledSections = questionnaireCompleted
    ? totalSections
    : hasActiveOptimistic && optimisticFilledSections > 0
      ? Math.min(totalSections, optimisticFilledSections)
      : (apiFilled ?? 0);

  return {
    totalSections,
    filledSections,
    percent,
    bePercent: apiPercent,
    optimisticPercent: optimistic,
  };
}
